import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff } from 'lucide-react';
import SocialLoginButtons from '../components/SocialLoginButtons';
import { trackPixelEvent } from '../hooks/useMetaPixel';
import LegalFooter from '../components/LegalFooter';
import LanguageSwitcher from '../components/LanguageSwitcher';

const RegisterPage = () => {
  const { t } = useTranslation('auth');
  const { register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ email: '', full_name: '', username: '', password: '', password_confirm: '' });
  // Phase F9.2 — declared date of birth (18+ only, computed server-side).
  const [dob, setDob] = useState({ dd: '', mm: '', yyyy: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [showPwdConfirm, setShowPwdConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  // Phase 18 — mandatory consent gate.
  const [acceptedTos, setAcceptedTos] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateDob = (k, max) => (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, max);
    setDob((d) => ({ ...d, [k]: v }));
  };
  const dobComplete = dob.dd.length >= 1 && dob.mm.length >= 1 && dob.yyyy.length === 4;

  // Phase 42 — realtime password confirmation state.
  const passwordMismatch =
    form.password_confirm.length > 0 && form.password !== form.password_confirm;
  const passwordConfirmed =
    form.password.length >= 6 && form.password === form.password_confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTos) {
      toast({
        title: t('register.tos_missing_title'),
        description: t('register.tos_missing_desc'),
        variant: 'destructive',
      });
      return;
    }
    if (passwordMismatch || form.password !== form.password_confirm) {
      toast({
        title: t('register.password_mismatch_title'),
        description: t('register.password_mismatch_desc'),
        variant: 'destructive',
      });
      return;
    }
    setLoading(true);
    try {
      // Do not send password_confirm to the backend — it's a client-only field.
      const { password_confirm: _drop, ...payload } = form;
      payload.date_of_birth = `${dob.yyyy.padStart(4, '0')}-${dob.mm.padStart(2, '0')}-${dob.dd.padStart(2, '0')}`;
      const result = await register(payload);
      // Phase 37E — event_id matches the server-side CAPI dedup key
      // (auth.py::register uses `signup_{user_id}`). Meta collapses
      // browser + server events into one CompleteRegistration.
      const dedupEventId = result?.user_id ? `signup_${result.user_id}` : undefined;
      trackPixelEvent(
        'CompleteRegistration',
        { content_name: 'user_signup' },
        dedupEventId,
      );
      // Phase 27.5 — Registration no longer logs the user in. The
      // backend now requires OTP verification; redirect to the
      // dedicated /verify-email page with the email pre-filled.
      const targetEmail = result?.email || form.email;
      toast({
        title: t('register.success_toast_title'),
        description: t('register.success_toast_desc', { email: targetEmail }),
      });
      navigate(`/verify-email?email=${encodeURIComponent(targetEmail)}`, { replace: true });
    } catch (err) {
      // Backend may return either a string detail (simple errors) or a
      // pydantic validation list ([{msg, loc, ...}]). React crashes if
      // we hand it the raw object — extract a readable string.
      const raw = err.response?.data?.detail;
      let description;
      if (typeof raw === 'string') {
        description = raw;
      } else if (Array.isArray(raw) && raw.length > 0) {
        description = raw.map((item) => item?.msg || String(item)).join(' · ');
      } else {
        description = t('register.error_default');
      }
      toast({
        title: t('register.error_title'),
        description,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    form.email &&
    form.full_name &&
    form.username.length >= 3 &&
    form.password.length >= 6 &&
    form.password === form.password_confirm &&
    dobComplete &&
    acceptedTos &&
    !loading;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10">
      <div className="mb-4 flex w-full max-w-sm justify-end">
        <LanguageSwitcher variant="full" align="end" testId="register-language-switcher" />
      </div>
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center border border-border bg-card px-10 py-8">
          <h1 className="mb-3 mt-2 text-5xl nymia-logo">
            {t('register.brand')}
          </h1>
          <p className="mb-5 text-center text-base font-semibold text-muted-foreground">
            {t('register.subtitle')}
          </p>
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-2" data-testid="register-form">
            <Input
              type="email"
              value={form.email}
              onChange={update('email')}
              placeholder={t('register.email_placeholder')}
              aria-label={t('register.email_label')}
              className="h-9 rounded-sm border-border bg-background text-xs"
              autoComplete="email"
              data-testid="register-email-input"
            />
            <Input
              type="text"
              value={form.full_name}
              onChange={update('full_name')}
              placeholder={t('register.full_name_placeholder')}
              aria-label={t('register.full_name_label')}
              className="h-9 rounded-sm border-border bg-background text-xs"
              autoComplete="name"
              data-testid="register-fullname-input"
            />
            <Input
              type="text"
              value={form.username}
              onChange={update('username')}
              placeholder={t('register.username_placeholder')}
              aria-label={t('register.username_label')}
              className="h-9 rounded-sm border-border bg-background text-xs"
              autoComplete="username"
              data-testid="register-username-input"
            />
            {/* Phase F9.2 — declared date of birth (Nymia is 18+) */}
            <div>
              <p className="mb-1 mt-1 text-left text-[11px] font-semibold text-muted-foreground">
                {t('register.dob_label')}
              </p>
              <div className="flex items-center gap-1.5">
                <Input
                  type="text"
                  value={dob.dd}
                  onChange={updateDob('dd', 2)}
                  placeholder={t('register.dob_day')}
                  inputMode="numeric"
                  autoComplete="bday-day"
                  className="h-9 w-1/4 rounded-sm border-border bg-background text-center text-xs"
                  data-testid="register-dob-day"
                />
                <span className="text-muted-foreground">/</span>
                <Input
                  type="text"
                  value={dob.mm}
                  onChange={updateDob('mm', 2)}
                  placeholder={t('register.dob_month')}
                  inputMode="numeric"
                  autoComplete="bday-month"
                  className="h-9 w-1/4 rounded-sm border-border bg-background text-center text-xs"
                  data-testid="register-dob-month"
                />
                <span className="text-muted-foreground">/</span>
                <Input
                  type="text"
                  value={dob.yyyy}
                  onChange={updateDob('yyyy', 4)}
                  placeholder={t('register.dob_year')}
                  inputMode="numeric"
                  autoComplete="bday-year"
                  className="h-9 flex-1 rounded-sm border-border bg-background text-center text-xs"
                  data-testid="register-dob-year"
                />
              </div>
              <p className="mt-1 text-left text-[10px] text-muted-foreground">
                {t('register.dob_hint')}
              </p>
            </div>
            <div className="relative">
              <Input
                type={showPwd ? 'text' : 'password'}
                value={form.password}
                onChange={update('password')}
                placeholder={t('register.password_placeholder')}
                aria-label={t('register.password_label')}
                className="h-9 rounded-sm border-border bg-background pr-14 text-xs"
                autoComplete="new-password"
                data-testid="register-password-input"
              />
              {form.password && (
                <button
                  type="button"
                  onClick={() => setShowPwd((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground"
                  data-testid="register-password-toggle"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>

            {/* Phase 42 — password confirmation with realtime validation. */}
            <div className="relative">
              <Input
                type={showPwdConfirm ? 'text' : 'password'}
                value={form.password_confirm}
                onChange={update('password_confirm')}
                placeholder={t('register.password_confirm_placeholder')}
                aria-label={t('register.password_confirm_label')}
                className={`h-9 rounded-sm bg-background pr-14 text-xs ${
                  passwordMismatch
                    ? 'border-red-500/70 focus-visible:ring-red-500/40'
                    : passwordConfirmed
                    ? 'border-emerald-500/60 focus-visible:ring-emerald-500/30'
                    : 'border-border'
                }`}
                autoComplete="new-password"
                data-testid="register-password-confirm-input"
              />
              {form.password_confirm && (
                <button
                  type="button"
                  onClick={() => setShowPwdConfirm((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground"
                  data-testid="register-password-confirm-toggle"
                >
                  {showPwdConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
            {passwordMismatch && (
              <p
                className="px-1 text-[11px] leading-snug text-red-400"
                data-testid="register-password-mismatch-hint"
              >
                {t('register.password_mismatch_hint')}
              </p>
            )}
            {passwordConfirmed && (
              <p
                className="px-1 text-[11px] leading-snug text-emerald-400"
                data-testid="register-password-match-hint"
              >
                {t('register.password_match_hint')}
              </p>
            )}

            {/* Phase 18 — mandatory T&C consent */}
            <label className="mt-2 flex items-start gap-2 px-1 text-[11px] leading-snug text-muted-foreground" htmlFor="register-tos">
              <Checkbox
                id="register-tos"
                checked={acceptedTos}
                onCheckedChange={(v) => setAcceptedTos(!!v)}
                className="mt-0.5"
                data-testid="register-tos-checkbox"
              />
              <span>
                {t('register.tos_confirm_18')}{' '}
                <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold gold-text hover:underline">{t('register.tos_terms_link')}</a>
                {t('register.tos_join_1')}{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold gold-text hover:underline">{t('register.tos_privacy_link')}</a>{' '}
                {t('register.tos_join_2')}{' '}
                <a href="/cookies" target="_blank" rel="noreferrer" className="font-semibold gold-text hover:underline">{t('register.tos_cookies_link')}</a>
                {t('register.tos_end')}
              </span>
            </label>

            <Button
              type="submit"
              disabled={!canSubmit}
              className="mt-3 h-8 rounded-md gold-gradient text-sm font-semibold disabled:opacity-60"
              data-testid="register-submit-button"
            >
              {loading ? t('register.submitting') : t('register.submit')}
            </Button>
          </form>

          <SocialLoginButtons />
        </div>

        <div className="mt-3 flex items-center justify-center border border-border bg-card py-6 text-sm">
          <span className="text-foreground">{t('register.already_have_account')}</span>
          <Link to="/login" className="ml-1 font-semibold gold-text hover:underline">
            {t('register.login_link')}
          </Link>
        </div>
      </div>

      <LegalFooter className="mt-8 max-w-md px-4" />
    </div>
  );
};

export default RegisterPage;
